import React, { useState } from 'react';
import { weeklyMinutes, validateProfileText } from '@/lib/auth-validation';
import backIcon from '../assets/back.svg';
import { LANGUAGES } from '@/constants/languages';

interface AlmostThereFormProps {
  initialLocation?: string;
  initialLanguages?: string[];
  initialAvailableHoursPerWeek?: number;
  onBack: (values: {
    location: string;
    languages: string[];
    availableHoursPerWeek?: number;
  }) => void;
  onSubmit: (values: {
    location: string;
    languages: string[];
    availableHoursPerWeek?: number;
  }) => void;
  isSubmitting: boolean;
  pending?: boolean;
  statusMessage?: string;
  errorMessage?: string;
}

export default function AlmostThereForm({
  initialLocation = '',
  initialLanguages = [],
  initialAvailableHoursPerWeek = 40,
  onBack,
  onSubmit,
  isSubmitting,
  pending = false,
  statusMessage,
  errorMessage,
}: AlmostThereFormProps) {
  const [location, setLocation] = useState(initialLocation);
  const [languages, setLanguages] = useState<string[]>(initialLanguages);
  const [availableHours, setAvailableHours] = useState<number>(
    initialAvailableHoursPerWeek
  );

  const [localError, setLocalError] = useState<string | null>(null);

  const handleSelectLanguage = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    if (code && !languages.includes(code)) {
      setLanguages([...languages, code]);
      setLocalError(null);
    }
    e.target.value = '';
  };

  const handleRemoveLanguage = (codeToRemove: string) => {
    setLanguages(languages.filter((c) => c !== codeToRemove));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLocalError(null);

    const trimmedLocation = location.trim();
    if (!trimmedLocation) {
      setLocalError('Location is required.');
      return;
    }

    if (languages.length === 0) {
      setLocalError('Please select at least one spoken language.');
      return;
    }

    try {
      weeklyMinutes(availableHours);
      validateProfileText(trimmedLocation, 'Location', 128, true);
      if (languages.length > 32)
        throw new Error('Choose at most 32 languages.');
    } catch (reason) {
      setLocalError(
        reason instanceof Error ? reason.message : 'Invalid availability.'
      );
      return;
    }

    onSubmit({
      location: trimmedLocation,
      languages,
      availableHoursPerWeek: availableHours,
    });
  };

  // Sort languages alphabetically by their readable name for display in dropdown options
  const sortedLanguageOptions = Object.entries(LANGUAGES).sort((a, b) =>
    a[1].localeCompare(b[1])
  );

  return (
    <form onSubmit={handleSubmit} aria-label="Availability">
      <div className="mb-6">
        <h1 className="mb-1 text-[30px] font-bold leading-[42px] text-[--text-dark-primary]">
          Almost there...
        </h1>
      </div>

      {/* Spoken Languages Input */}
      <div>
        <label
          htmlFor="languageSelect"
          className="mb-2 block text-sm font-medium text-[--text-dark-secondary]"
        >
          Spoken languages
        </label>

        {/* Selected Languages Chips */}
        {languages.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2 rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-2] p-3">
            {languages.map((code) => {
              const name = LANGUAGES[code] || code;
              return (
                <div
                  key={code}
                  className="flex h-[28px] !w-auto items-center gap-1.5 rounded-[8px] border border-[#3D3D3D] bg-[#141414] px-3"
                >
                  <span className="text-xs font-medium text-[#F5F5F5]">
                    {name}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove language ${name}`}
                    onClick={() => handleRemoveLanguage(code)}
                    disabled={isSubmitting || pending}
                    className="flex items-center border-none bg-transparent p-0 hover:opacity-75 focus:outline-none"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="rgba(255,255,255,0.6)"
                      strokeWidth="2.5"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="relative w-full">
          <select
            id="languageSelect"
            onChange={handleSelectLanguage}
            disabled={isSubmitting || pending}
            defaultValue=""
            className="h-[44px] w-full rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-1] pl-4 pr-10 text-[15px] text-[--text-dark-primary] outline-none transition-colors [color-scheme:dark] focus:border-emerald-500 [&>option]:bg-[--base-surface-1] [&>option]:text-[--text-dark-primary]"
            style={{
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23f5f5f5' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 16px center',
              backgroundSize: '16px',
            }}
          >
            <option value="" disabled>
              Select the languages you speak
            </option>
            {sortedLanguageOptions.map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Location Input */}
      <div>
        <label
          htmlFor="locationInput"
          className="mb-2 block text-sm font-medium text-[--text-dark-secondary]"
        >
          Location
        </label>
        <input
          id="locationInput"
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          required
          disabled={isSubmitting || pending}
          className="h-[44px] w-full rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-1] px-4 text-[15px] font-medium text-[--text-dark-primary] outline-none transition-colors focus:border-emerald-500"
          placeholder="Where are you based?"
        />
        <p className="mt-1 text-xs text-[rgba(255,255,255,0.5)]">
          City, Country
        </p>
      </div>

      {/* Explicit weekly capacity */}
      {
        <div className="animate-fadeIn">
          <label
            htmlFor="hoursInput"
            className="mb-2 block text-sm font-medium text-[--text-dark-secondary]"
          >
            Available hours per week
          </label>
          <input
            id="hoursInput"
            type="number"
            min="0"
            max="168"
            value={Number.isNaN(availableHours) ? '' : availableHours}
            onChange={(e) =>
              setAvailableHours(
                e.target.value === '' ? NaN : Number(e.target.value)
              )
            }
            required
            disabled={isSubmitting || pending}
            className="h-[44px] w-full rounded-[--radi-6] border border-[--base-border] bg-[--base-surface-1] px-4 text-[15px] font-medium text-[--text-dark-primary] outline-none transition-colors focus:border-emerald-500"
            placeholder="e.g. 20"
          />
          <p className="mt-1 text-xs text-[rgba(255,255,255,0.5)]">
            Whole hours from 0 to 168. Zero means unavailable. This capacity
            repeats weekly until you change it.
          </p>
        </div>
      }

      {/* Error Message */}
      {(localError || errorMessage) && (
        <div className="rounded-[--radi-6] border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.15)] p-3">
          <p role="alert" className="text-sm text-[#ef4444]">
            {localError || errorMessage}
          </p>
        </div>
      )}

      {/* Status Message */}
      {statusMessage && !errorMessage && (
        <p
          role="status"
          className="animate-pulse text-center text-sm text-[--text-dark-secondary]"
        >
          {statusMessage}
        </p>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4">
        {/* Back Button */}
        <button
          type="button"
          onClick={() =>
            onBack({
              location,
              languages,
              availableHoursPerWeek: Number.isNaN(availableHours)
                ? undefined
                : availableHours,
            })
          }
          disabled={isSubmitting || pending}
          className="flex h-[44px] cursor-pointer items-center justify-center gap-2 rounded-[--radi-6] border border-[--base-border] bg-[#231F1F] px-4 text-base font-semibold text-[--text-dark-primary] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <img src={backIcon} width={24} height={24} alt="" />
          Back
        </button>

        {/* Continue Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex h-[44px] cursor-pointer items-center justify-center gap-2 rounded-[--radi-6] border-none bg-[--state-brand-active] px-6 text-base font-semibold text-[--text-light-primary] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting
            ? 'Checking registration…'
            : pending
              ? 'Check registration'
              : 'Complete registration'}
        </button>
      </div>
    </form>
  );
}
